
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { getPendingResellerOrders, approveOrder, rejectOrder, holdOrder } from '@/utils/resellerOrderService';
import { ResellerOrder } from '@/types/resellerOrder';
import OrderStatsCards from './OrderStatsCards';
import OrdersTable from './OrdersTable';
import OrderDetailsDialog from './OrderDetailsDialog';

const PendingOrdersTable = () => {
  const { toast } = useToast();
  const [pendingOrders, setPendingOrders] = useState<ResellerOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<ResellerOrder | null>(null);
  const [isOrderDetailsOpen, setIsOrderDetailsOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingOrderId, setProcessingOrderId] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrders = async () => {
      setIsLoading(true);
      try {
        const orders = await getPendingResellerOrders();
        setPendingOrders(orders);
      } catch (error) {
        console.error('Error fetching pending orders:', error);
        toast({
          title: 'Error',
          description: 'Failed to load pending orders',
          variant: 'destructive'
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, [toast]);

  const filteredOrders = pendingOrders.filter(order => 
    order.businessName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.contactName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.contactEmail.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOrderClick = (order: ResellerOrder, e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    setSelectedOrder(order);
    setIsOrderDetailsOpen(true);
  };

  const handleApproveOrder = async (orderId: string) => {
    setIsProcessing(true);
    setProcessingOrderId(orderId);
    try {
      // Fix: Add a required reason parameter (empty string as default)
      await approveOrder(orderId, "");
      
      setPendingOrders(pendingOrders.filter(order => order.id !== orderId));
      
      toast({
        title: 'Order Approved',
        description: `Order has been approved and a license key has been generated.`
      });
    } catch (error) {
      console.error('Error approving order:', error);
      toast({
        title: 'Error',
        description: 'Failed to approve order',
        variant: 'destructive'
      });
    } finally {
      setIsProcessing(false);
      setProcessingOrderId(null);
    }
  };

  const handleRejectOrder = async (orderId: string) => {
    setIsProcessing(true);
    setProcessingOrderId(orderId);
    try {
      // Fix: Add a required reason parameter (empty string as default)
      await rejectOrder(orderId, "Order rejected");
      
      setPendingOrders(pendingOrders.filter(order => order.id !== orderId));
      
      toast({
        title: 'Order Rejected',
        description: `Order has been rejected.`
      });
    } catch (error) {
      console.error('Error rejecting order:', error);
      toast({
        title: 'Error',
        description: 'Failed to reject order',
        variant: 'destructive'
      });
    } finally {
      setIsProcessing(false);
      setProcessingOrderId(null);
    }
  };

  const handleHoldOrder = async (orderId: string) => {
    setIsProcessing(true);
    setProcessingOrderId(orderId);
    try {
      // Fix: Add a required reason parameter (empty string as default)
      await holdOrder(orderId, "Order on hold");
      
      setPendingOrders(pendingOrders.filter(order => order.id !== orderId));
      
      toast({
        title: 'Order On Hold',
        description: `Order has been placed on hold for further review.`
      });
    } catch (error) {
      console.error('Error holding order:', error);
      toast({
        title: 'Error',
        description: 'Failed to place order on hold',
        variant: 'destructive'
      });
    } finally {
      setIsProcessing(false);
      setProcessingOrderId(null);
    }
  };

  return (
    <div className="space-y-6">
      <OrderStatsCards pendingOrders={pendingOrders} />

      <Card>
        <CardHeader>
          <CardTitle>Pending Orders</CardTitle>
          <CardDescription>
            Review and process new reseller orders that require your approval
          </CardDescription>
        </CardHeader>
        <CardContent>
          <OrdersTable
            isLoading={isLoading}
            orders={filteredOrders}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onOrderClick={handleOrderClick}
            onApproveOrder={handleApproveOrder}
            onRejectOrder={handleRejectOrder}
            onHoldOrder={handleHoldOrder}
            processingOrderId={processingOrderId}
            isProcessing={isProcessing}
          />
        </CardContent>
      </Card>

      <OrderDetailsDialog
        isOpen={isOrderDetailsOpen}
        onOpenChange={setIsOrderDetailsOpen}
        selectedOrder={selectedOrder}
        onApproveOrder={handleApproveOrder}
        onRejectOrder={handleRejectOrder}
        onHoldOrder={handleHoldOrder}
        isProcessing={isProcessing}
      />
    </div>
  );
};

export default PendingOrdersTable;
